import { posthog } from "posthog-js";
import { hasAnalyticsConsent } from "@/lib/cookieConsent";

export const POSTHOG_OPT_IN_EVENT = "equiply:posthog-opt-in";

let isInitialized = false;

const POSTHOG_KEY = "phc_e635Jh0YVyxYzKwmrRMfjW275KC3MjemW1QxvzoAgRR";
const POSTHOG_HOST = "https://eu.i.posthog.com";

const getTrafficEnvironment = (hostname: string) => {
  if (hostname === "localhost" || hostname === "127.0.0.1") return "local";
  if (hostname.endsWith(".vercel.app") || hostname.includes("staging")) return "staging";
  return "production";
};

const getPosthogContextProperties = (): Record<string, unknown> => {
  if (typeof window === "undefined") return {};

  const { origin, hostname, port } = window.location;
  return {
    traffic_origin: origin,
    traffic_host: hostname,
    traffic_port: port || null,
    traffic_environment: getTrafficEnvironment(hostname),
  };
};

export const ensurePosthogInit = () => {
  if (isInitialized) return;

  // Rien avant « Accepter » : ni cookie, ni stockage, ni appel réseau (CNIL).
  if (!hasAnalyticsConsent()) return;

  const apiKey = POSTHOG_KEY;
  const apiHost = POSTHOG_HOST;

  // If not configured, do nothing (prevents runtime crashes in dev).
  if (!apiKey || !apiHost) return;

  posthog.init(apiKey, {
    api_host: apiHost,
    opt_out_capturing_by_default: true,
    capture_pageview: true,
    // Keep initial payloads small; we'll rely on manual events for feature adoption.
    autocapture: false,
    // Avoid loading surveys.js (uses zustand; default import triggers console deprecation noise).
    // Set to false if you use PostHog Surveys in this project.
    disable_surveys: true,
    // Les enregistrements de session ne montrent ni textes ni saisies : les écrans affichent
    // noms, horaires et salaires de salariés, données que l'employeur nous confie.
    session_recording: { maskAllInputs: true, maskTextSelector: "*" },
    enable_recording_console_log: false,
    mask_all_text: true,
    mask_all_element_attributes: true,
    // Jetons d'activation ou de réinitialisation présents dans certaines URL.
    mask_personal_data_properties: true,
    custom_personal_data_properties: ["token_hash", "token", "code", "email"],
  });

  // Register stable traffic metadata globally for easier dashboard filtering.
  posthog.register(getPosthogContextProperties());

  isInitialized = true;

  // The user accepted cookies (now or in the past): opt-in immediately.
  try {
    if (typeof posthog.has_opted_in_capturing === "function" && !posthog.has_opted_in_capturing()) {
      posthog.opt_in_capturing();
    }
    window.dispatchEvent(new Event(POSTHOG_OPT_IN_EVENT));
  } catch {
    // No-op (analytics is best-effort)
  }
};

/** Efface du navigateur ce que PostHog y a gardé (cookie et stockage `ph_…`). */
const clearPosthogBrowserStorage = () => {
  for (const storage of [localStorage, sessionStorage]) {
    try {
      Object.keys(storage)
        .filter((key) => key.startsWith("ph_") || key.startsWith("__ph"))
        .forEach((key) => storage.removeItem(key));
    } catch {
      // No-op
    }
  }

  try {
    const parentDomain = window.location.hostname.split(".").slice(-2).join(".");
    document.cookie
      .split(";")
      .map((cookie) => cookie.split("=")[0].trim())
      .filter((name) => name.startsWith("ph_") || name.startsWith("__ph"))
      .forEach((name) => {
        document.cookie = `${name}=; Max-Age=0; path=/`;
        document.cookie = `${name}=; Max-Age=0; path=/; domain=.${parentDomain}`;
      });
  } catch {
    // No-op
  }
};

/** Retrait du consentement : coupe la capture et efface les traces locales de PostHog. */
export const posthogOptOut = () => {
  if (isInitialized) {
    try {
      posthog.opt_out_capturing();
    } catch {
      // No-op
    }
  }
  clearPosthogBrowserStorage();
};

/** À appeler juste après « Accepter » : l'initialisation active la capture et prévient les écouteurs. */
export const posthogOptInCapturing = () => {
  ensurePosthogInit();
};

export const identifyPosthog = (distinctId: string, properties?: Record<string, unknown>) => {
  try {
    ensurePosthogInit();
    if (!isInitialized) return;
    posthog.identify(distinctId, {
      ...getPosthogContextProperties(),
      ...(properties ?? {}),
    });
  } catch {
    // No-op
  }
};

export const capturePosthog = (eventName: string, properties?: Record<string, unknown>) => {
  try {
    ensurePosthogInit();
    if (!isInitialized) return;
    posthog.capture(eventName, {
      ...getPosthogContextProperties(),
      ...(properties ?? {}),
    });
  } catch {
    // No-op
  }
};

